import React from "react";
import { Box, TextField, MenuItem } from "@material-ui/core";
import EditableField from "../../../../EditableField/EditableField";

// Bloques genéricos (heading/text/button/image/link/divider/spacer) y su
// formulario de edición, compartidos entre el editor del correo de
// confirmación (EmailBlockBuilder.jsx) y el compositor de correos de
// campaña (Campaign/CampaignEmailContainer.jsx) — ambos usan exactamente el
// mismo esquema de bloque y lo renderiza el mismo
// functions/modules/emailBlocks/genericBlocks.js del lado del backend.
export const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export const BLOQUES_GENERICOS = [
  { type: "heading", label: "Título" },
  { type: "text", label: "Texto" },
  { type: "button", label: "Botón" },
  { type: "image", label: "Imagen" },
  { type: "link", label: "Link" },
  { type: "divider", label: "Separador" },
  { type: "spacer", label: "Espacio" },
];

export function bloqueGenericoPorDefecto(type) {
  switch (type) {
    case "heading":
      return { id: uid(), type, text: "Título", size: 22, align: "center" };
    case "text":
      return { id: uid(), type, text: "Escribe aquí, puedes usar [nombres] [apellidos] etc.", size: 14, color: "#111827", align: "left" };
    case "button":
      return { id: uid(), type, text: "Ver más", href: "https://", align: "center" };
    case "image":
      return { id: uid(), type, src: "", alt: "", width: 212, align: "center" };
    case "link":
      return { id: uid(), type, value: "https://" };
    case "divider":
      return { id: uid(), type, thickness: 1, color: "#e5e7eb" };
    case "spacer":
      return { id: uid(), type, height: 16 };
    default:
      return { id: uid(), type: "text", text: "" };
  }
}

export function EditorBloqueGenerico({ block, eventoIndex, onChange }) {
  const alignField = (value, onChangeAlign) => (
    <TextField
      select
      label="Alineación"
      value={value || "left"}
      onChange={(e) => onChangeAlign(e.target.value)}
      margin="dense"
      style={{ width: 140 }}
    >
      <MenuItem value="left">Izquierda</MenuItem>
      <MenuItem value="center">Centro</MenuItem>
      <MenuItem value="right">Derecha</MenuItem>
    </TextField>
  );

  switch (block.type) {
    case "heading":
      return (
        <Box>
          <TextField
            label="Texto"
            fullWidth
            margin="dense"
            value={block.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
            helperText="Puedes usar datos del atleta entre corchetes, por ejm [nombres] [apellidos]"
          />
          <Box display="flex" style={{ gap: 8 }} alignItems="center">
            <TextField
              label="Tamaño"
              type="number"
              margin="dense"
              value={block.size || 22}
              onChange={(e) => onChange({ size: Number(e.target.value) })}
              style={{ width: 100 }}
            />
            {alignField(block.align, (v) => onChange({ align: v }))}
          </Box>
        </Box>
      );
    case "text":
      return (
        <Box>
          <TextField
            label="Texto"
            fullWidth
            multiline
            rows={3}
            margin="dense"
            value={block.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
            helperText="Puedes usar datos del atleta entre corchetes, por ejm [nombres] [apellidos]"
          />
          <Box display="flex" style={{ gap: 8 }} alignItems="center">
            <TextField
              label="Tamaño"
              type="number"
              margin="dense"
              value={block.size || 14}
              onChange={(e) => onChange({ size: Number(e.target.value) })}
              style={{ width: 100 }}
            />
            {alignField(block.align, (v) => onChange({ align: v }))}
          </Box>
          <Box mt={1} style={{ maxWidth: 220 }}>
            <EditableField
              fieldObject={{
                fieldName: "color",
                label: "Color del texto",
                type: "color",
                defaultValue: block.color || "#111827",
              }}
              onSubmit={(values) => onChange(values)}
            />
          </Box>
        </Box>
      );
    case "button":
      return (
        <Box>
          <TextField
            label="Texto del botón"
            fullWidth
            margin="dense"
            value={block.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
          />
          <TextField
            label="Link"
            fullWidth
            margin="dense"
            value={block.href || ""}
            onChange={(e) => onChange({ href: e.target.value })}
          />
          {alignField(block.align, (v) => onChange({ align: v }))}
        </Box>
      );
    case "image":
      return (
        <Box>
          <EditableField
            fieldObject={{
              fieldName: "src",
              label: "Imagen",
              type: "photo",
              defaultValue: block.src || "",
              route: "correoConfirmacion/" + eventoIndex,
            }}
            onSubmit={(values) => onChange(values)}
          />
          <Box display="flex" style={{ gap: 8 }} alignItems="center">
            <TextField
              label="Texto alternativo"
              margin="dense"
              value={block.alt || ""}
              onChange={(e) => onChange({ alt: e.target.value })}
              style={{ flex: 1 }}
            />
            <TextField
              label="Ancho (px)"
              type="number"
              margin="dense"
              value={block.width || 212}
              onChange={(e) => onChange({ width: Number(e.target.value) })}
              style={{ width: 120 }}
            />
          </Box>
          {alignField(block.align, (v) => onChange({ align: v }))}
        </Box>
      );
    case "link":
      return (
        <TextField
          label="Link"
          fullWidth
          margin="dense"
          value={block.value || ""}
          onChange={(e) => onChange({ value: e.target.value })}
        />
      );
    case "divider":
      return (
        <Box display="flex" style={{ gap: 16 }} alignItems="flex-start">
          <TextField
            label="Grosor (px)"
            type="number"
            margin="dense"
            value={block.thickness || 1}
            onChange={(e) => onChange({ thickness: Number(e.target.value) })}
            style={{ width: 120 }}
          />
          <Box style={{ maxWidth: 220 }}>
            <EditableField
              fieldObject={{
                fieldName: "color",
                label: "Color",
                type: "color",
                defaultValue: block.color || "#e5e7eb",
              }}
              onSubmit={(values) => onChange(values)}
            />
          </Box>
        </Box>
      );
    case "spacer":
      return (
        <TextField
          label="Alto (px)"
          type="number"
          margin="dense"
          value={block.height || 16}
          onChange={(e) => onChange({ height: Number(e.target.value) })}
          style={{ width: 120 }}
        />
      );
    default:
      return null;
  }
}
